import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { Button, Space, Typography, Card, Alert } from 'antd';
import { useTranslation } from 'react-i18next';
import i18n from '../../../shared/config/i18n';
import { 
  BookOutlined, 
  UserOutlined, 
  CheckOutlined, 
  EditOutlined, 
  PlayCircleOutlined,
  PauseCircleOutlined,
  CopyOutlined,
  LikeOutlined,
  DislikeOutlined,
  LoadingOutlined
} from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import styled from 'styled-components';
import { Message } from '../../../shared/types';
import { StrategySelectionMessage } from './StrategySelectionMessage';
import { StrategyModificationPrompt } from './StrategyModificationPrompt';

const { Text, Paragraph } = Typography;

// 样式组件
const MessageContainer = styled.div<{ $isUser: boolean }>`
  display: flex;
  margin-bottom: 16px;
  flex-direction: row;
  align-items: flex-start;
  gap: 0; /* 所有消息都无头像，不需要间距 */
  justify-content: ${props => props.$isUser ? 'flex-end' : 'flex-start'};
  
  /* 移动端适配 */
  @media (max-width: 768px) {
    margin-bottom: 12px; /* 移动端减少消息间距 */
    /* 移动端布局优化 */
    padding: 0 8px; /* 添加左右边距 */
    width: 100%;
    box-sizing: border-box;
  }
`;

const Avatar = styled.div<{ $isUser: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: none; /* 隐藏所有头像 */
  align-items: center;
  justify-content: center;
  background: ${props => props.$isUser ? '#1890ff' : '#52c41a'};
  color: white;
  font-size: 16px;
  flex-shrink: 0;
`;

const MessageContent = styled.div<{ $isUser: boolean }>`
  flex: 1;
  max-width: ${props => props.$isUser ? '80%' : '95%'}; /* 统一：用户消息80%，AI回答95% */
  background: ${props => props.$isUser ? '#f0f9f0' : '#f6f6f6'}; /* 用户消息改为淡绿色 */
  color: ${props => props.$isUser ? '#2d3748' : '#000'}; /* 用户消息文字改为深色 */
  padding: 12px 16px;
  border-radius: 8px;
  word-wrap: break-word;
  border: ${props => props.$isUser ? '1px solid #d9f7be' : 'none'}; /* 添加淡绿色边框 */
  text-align: left; /* 所有内容左对齐 */
  font-size: 14px; /* 字体小一号，从默认16px改为14px */
  line-height: 1.6; /* 增加行高保持可读性 */
  
  /* 移动端适配 */
  @media (max-width: 768px) {
    max-width: ${props => props.$isUser ? '80%' : '95%'}; /* 统一：用户消息80%，AI回答95% */
    padding: 10px 12px;
    font-size: 13px; /* 移动端字体再小一点 */
    line-height: 1.5;
    /* 防止内容导致页面变宽 */
    width: 100% !important;
    min-width: 0 !important;
    overflow-wrap: break-word !important;
    word-break: break-word !important;
    hyphens: auto !important;
    -webkit-hyphens: auto !important;
    -moz-hyphens: auto !important;
    -ms-hyphens: auto !important;
    /* 确保所有子元素不会溢出 */
    * {
      max-width: 100% !important;
      word-wrap: break-word !important;
      overflow-wrap: break-word !important;
    }
  }
  
  /* 确保所有文本内容都使用小一号字体 */
  p, div, span, li, ol, ul, h1, h2, h3, h4, h5, h6 {
    font-size: 14px !important;
    
    @media (max-width: 768px) {
      font-size: 13px !important;
      max-width: 100% !important;
      word-wrap: break-word !important;
      overflow-wrap: break-word !important;
    }
  }
  
  /* 保持代码块字体稍小 */
  pre, code {
    font-size: 13px !important;
    max-width: 100% !important;
    overflow-x: auto !important;
    white-space: pre-wrap !important;
    word-wrap: break-word !important;
    
    @media (max-width: 768px) {
      font-size: 12px !important;
      max-width: 100% !important;
      overflow-x: auto !important;
      white-space: pre-wrap !important;
      word-wrap: break-word !important;
    }
  }
  
  /* 表格处理 */
  table {
    max-width: 100% !important;
    table-layout: fixed !important;
    word-wrap: break-word !important;
    
    @media (max-width: 768px) {
      font-size: 12px !important;
    }
  }
  
  /* 图片处理 */
  img {
    max-width: 100% !important;
    height: auto !important;
  }
  
  /* 链接处理 */
  a {
    word-wrap: break-word !important;
    overflow-wrap: break-word !important;
  }
  
  ${props => props.$isUser && `
    .ant-typography {
      color: #2d3748 !important; /* 确保用户消息文字为深色 */
      font-size: 14px !important; /* 确保Antd Typography组件也使用小字体 */
      
      @media (max-width: 768px) {
        font-size: 13px !important;
      }
    }
  `}
`;

const StreamingIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #666;
  font-style: italic;
  margin-top: 8px;
`;

const ClarificationHint = styled.div`
  background: #e6f7ff;
  border: 1px solid #91d5ff;
  border-radius: 6px;
  padding: 12px;
  margin-top: 12px;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 13px; /* 新增：整体调小ClarificationHint内字体 */
  
  .anticon {
    color: #1890ff;
    margin-top: 2px;
  }
  
  /* 移动端适配 */
  @media (max-width: 768px) {
    max-width: 90%; /* 移动端限制宽度 */
    padding: 10px;
    font-size: 12px;
    flex-direction: column;
    gap: 6px;
  }
`;

const ConfirmationActions = styled.div`
  margin-top: 16px;
  text-align: left; /* 按钮也左对齐 */
  
  .ant-btn {
    margin: 0 8px 8px 0; /* 调整按钮间距 */
  }
  
  /* 移动端适配 */
  @media (max-width: 768px) {
    max-width: 90%; /* 移动端限制宽度 */
  }
`;

const ForceAnalysisOption = styled.div`
  margin-top: 8px;
  padding: 8px 12px;
  background: #fff7e6;
  border: 1px solid #ffd666;
  border-radius: 4px;
  font-size: 12px;
  
  .warning-text {
    color: #fa8c16;
    margin: 0 4px;
  }
  
  .force-btn {
    margin-left: 8px;
    padding: 2px 8px;
    font-size: 11px;
  }
  
  /* 移动端适配 */
  @media (max-width: 768px) {
    max-width: 90%; /* 移动端限制宽度 */
    padding: 6px 10px;
    font-size: 11px;
    
    .force-btn {
      margin-left: 6px;
      padding: 2px 6px;
      font-size: 10px;
    }
  }
`;

const MessageActions = styled.div`
  margin-top: 8px;
  display: flex;
  gap: 8px;
  justify-content: flex-start; /* 操作按钮左对齐 */
  opacity: 0.7;
  
  .action-btn {
    border: none;
    background: none;
    color: #666;
    cursor: pointer;
    padding: 8px 12px; /* 增加内边距 */
    border-radius: 6px;
    font-size: 14px; /* 增大字体 */
    min-width: 36px; /* 设置最小宽度 */
    min-height: 32px; /* 设置最小高度 */
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    
    &:hover {
      background: rgba(0, 0, 0, 0.1);
      opacity: 1;
      transform: scale(1.05); /* 悬停时轻微放大 */
    }
  }
  
  /* 移动端适配 */
  @media (max-width: 768px) {
    max-width: 90%; /* 移动端限制宽度 */
    gap: 6px;
    
    .action-btn {
      padding: 6px 10px;
      font-size: 13px;
      min-width: 32px;
      min-height: 28px;
    }
  }
`;

const FileAttachment = styled.div`
  margin-top: 8px;
  padding: 8px 12px;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  font-size: 13px;
  color: #495057;
  display: flex;
  align-items: center;
  gap: 8px;
  
  /* 移动端适配 */
  @media (max-width: 768px) {
    max-width: 90%; /* 移动端限制宽度 */
    padding: 6px 10px;
    font-size: 12px;
    gap: 6px;
  }
`;

interface ChatMessageProps {
  message: Message;
  onConfirmCaseInfo?: () => void;
  onForceStartAnalysis?: () => void;
  onModifyCaseInfo?: () => void;
  onStrategySelect?: (strategyId: string) => void;
  onModificationDecision?: (decision: 'complete' | 'modify') => void;
  onReselectStrategy?: () => void; // 新增：重新选择策略
}

export const ChatMessage: React.FC<ChatMessageProps> = ({
  message,
  onConfirmCaseInfo,
  onForceStartAnalysis,
  onModifyCaseInfo,
  onStrategySelect,
  onModificationDecision,
  onReselectStrategy,
}) => {
  const { t, i18n } = useTranslation();
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // 检测是否为移动端
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  /**
   * 优化的Markdown组件，使用useMemo缓存渲染结果
   * 
   * 解决方案组合：
   * 1. remark-breaks: 处理换行符，将 \n 转换为 <br>
   * 2. 内容预处理：清理格式问题
   * 3. 图标修复：修复emoji显示问题
   */
  const OptimizedMarkdown = useMemo(() => {
    const MarkdownComponent = ({ content }: { content: string }) => {
      const containerRef = useRef<HTMLDivElement>(null);
      
      // React友好的硬编码修复特定的图标分离问题
      const fixSpecificIconSeparation = useCallback((container: HTMLElement) => {
        // 用户建议的简单方案：删除图标后的换行符，让图标自然与下一行文字合并
        
        // 查找所有只包含单个图标的元素
        const allElements = Array.from(container.querySelectorAll('*'));
        
        allElements.forEach(element => {
          const text = element.textContent?.trim() || '';
          
          // 检查是否是只包含单个图标的元素
          if (text.length <= 2 && /^[📋🏛️⚖️⚠️✅💡🔄📎⚡🎯🔍📊📈📉]$/.test(text)) {
            // 找到下一个兄弟元素
            const nextElement = element.nextElementSibling;
            
            if (nextElement) {
              const nextText = nextElement.textContent?.trim() || '';
              
              // 如果下一个元素有文字内容，就合并
              if (nextText.length > 0) {
                try {
                  // 将图标添加到下一个元素的开头
                  nextElement.innerHTML = text + ' ' + nextElement.innerHTML;
                  // 隐藏原来的图标元素
                  (element as HTMLElement).style.display = 'none';
                } catch (error) {
                  // 静默处理错误
                }
              }
            }
          }
        });
      }, []);
      
      // 修复emoji图标显示问题的函数
      const fixEmojiDisplay = useCallback((container: HTMLElement) => {
        if (!container) return;
        
        const textNodes: Text[] = [];
        const walker = document.createTreeWalker(
          container,
          NodeFilter.SHOW_TEXT,
          null
        );
        
        let node;
        while (node = walker.nextNode()) {
          textNodes.push(node as Text);
        }
        
        textNodes.forEach(textNode => {
          let text = textNode.textContent || '';
          
          // 处理emoji后的换行符问题
          // 1. 将emoji后面紧跟的换行符替换为空格（避免不必要的换行）
          text = text.replace(/(📋|🏛️|⚖️|⚠️|✅|💡|🔄|📎|⚡|🎯|🔍|📊|📈|📉)\n+/g, '$1 ');
          
          // 2. 确保emoji和后续文本之间有适当的空格
          text = text.replace(/(📋|🏛️|⚖️|⚠️|✅|💡|🔄|📎|⚡|🎯|🔍|📊|📈|📉)([a-zA-Z0-9])/g, '$1 $2');
          
          // 3. 特别处理章节标题中的图标间距问题
          text = text.replace(/(##\s*)(📋|🏛️|⚖️|⚠️|✅|💡|🔄|📎|⚡|🎯|🔍|📊|📈|📉)([a-zA-Z0-9])/g, '$1$2 $3');
          
          // 4. 移除图标前的多余空白字符，但保留必要的换行
          text = text.replace(/[ \t]+(📋|🏛️|⚖️|⚠️|✅|💡|🔄|📎|⚡|🎯|🔍|📊|📈|📉)/g, ' $1');
          
          // 5. 处理段落开头的图标，确保不会有多余的换行
          text = text.replace(/\n(📋|🏛️|⚖️|⚠️|✅|💡|🔄|📎|⚡|🎯|🔍|📊|📈|📉)\n/g, '\n$1 ');
          
          if (text !== textNode.textContent) {
            textNode.textContent = text;
          }
        });
        
        // 额外处理：确保标题中的图标不会被不当换行
        const headings = container.querySelectorAll('h1, h2, h3, h4, h5, h6');
        headings.forEach(heading => {
          const text = heading.textContent || '';
          // 确保标题中图标和文字之间有且仅有一个空格
          const cleanedText = text.replace(/(📋|🏛️|⚖️|⚠️|✅|💡|🔄|📎|⚡|🎯|🔍|📊|📈|📉)\s*([a-zA-Z0-9])/g, '$1 $2');
          if (cleanedText !== text) {
            heading.textContent = cleanedText;
          }
        });
        
        // 最后处理：移除HTML中可能存在的emoji后换行问题
        const allElements = container.querySelectorAll('*');
        allElements.forEach(element => {
          if (element.childNodes.length > 0) {
            for (let i = 0; i < element.childNodes.length; i++) {
              const child = element.childNodes[i];
              if (child.nodeType === Node.TEXT_NODE) {
                let text = child.textContent || '';
                // 处理文本节点中emoji后的换行
                text = text.replace(/(📋|🏛️|⚖️|⚠️|✅|💡|🔄|📎|⚡|🎯|🔍|📊|📈|📉)\s*\n\s*/g, '$1 ');
                if (text !== child.textContent) {
                  child.textContent = text;
                }
              }
            }
          }
        });

        // 修复###分离显示的问题（将分离的###和下面的内容合并）
        const fixHashtagDisplay = () => {
          const walker = document.createTreeWalker(
            container,
            NodeFilter.SHOW_TEXT,
            null
          );
          
          const textNodes: Text[] = [];
          let node;
          while (node = walker.nextNode()) {
            textNodes.push(node as Text);
          }
          
          textNodes.forEach(textNode => {
            let text = textNode.textContent || '';
            
            // 特殊情况：处理分割的图标（如### ⚖ 和下一行的 ️ 文字）
            text = text.replace(/^#{3}\s*([⚖📋🏛⚠✅💡🔄📎⚡🎯🔍📊📈📉])\s*\n+([️].*)/gm, '### $1$2');
            text = text.replace(/\n#{3}\s*([⚖📋🏛⚠✅💡🔄📎⚡🎯🔍📊📈📉])\s*\n+([️].*)/g, '\n### $1$2');
            
            // 将分离的###和下一行内容合并
            // 匹配###后面有换行然后是图标或文字的情况
            text = text.replace(/^#{3}\s*\n+([📋🏛️⚖️⚠️✅💡🔄📎⚡🎯🔍📊📈📉].*)/gm, '### $1');
            text = text.replace(/^#{3}\s*\n+([^\n#].*)/gm, '### $1');
            
            // 处理文档中间的分离情况
            text = text.replace(/\n#{3}\s*\n+([📋🏛️⚖️⚠️✅💡🔄📎⚡🎯🔍📊📈📉].*)/g, '\n### $1');
            text = text.replace(/\n#{3}\s*\n+([^\n#].*)/g, '\n### $1');
            
            if (text !== textNode.textContent) {
              textNode.textContent = text;
            }
          });
          
                     // 处理已渲染的HTML元素
           const allElements = Array.from(container.querySelectorAll('*'));
           for (let i = 0; i < allElements.length; i++) {
             const element = allElements[i];
             if (element.tagName && element.tagName.toLowerCase().startsWith('h')) {
               const text = element.textContent || '';
               
               // 特殊情况：处理### + 分割图标的情况（如### ⚖）
               if (/^#{3}\s*[⚖📋🏛⚠✅💡🔄📎⚡🎯🔍📊📈📉]\s*$/.test(text)) {
                 const nextElement = element.nextElementSibling;
                 if (nextElement && nextElement.textContent && nextElement.textContent.startsWith('️')) {
                   // 合并分割的图标和文字
                   element.textContent = `### ${text.replace(/^#{3}\s*/, '')}${nextElement.textContent.trim()}`;
                   nextElement.remove();
                 }
               }
               // 如果标题元素只包含###，检查下一个元素是否可以合并
               else if (/^\s*#{3}\s*$/.test(text)) {
                 const nextElement = element.nextElementSibling;
                 if (nextElement && nextElement.textContent) {
                   // 将下一个元素的内容合并到当前标题
                   element.textContent = `### ${nextElement.textContent.trim()}`;
                   nextElement.remove();
                 } else {
                   // 如果没有下一个元素，则移除空的###标题
                   element.remove();
                 }
               }
             }
           }
        };
        
        // 执行###修复
        fixHashtagDisplay();

        // React友好的图标分离问题修复
        fixSpecificIconSeparation(container);
      }, [fixSpecificIconSeparation]);
      
      // 内容预处理函数 - 修复Markdown格式问题
      const preprocessContent = useCallback((rawContent: string): string => {
        let processedContent = rawContent;
        
        // 1. 清理多余的换行符（但保留段落分隔）
        processedContent = processedContent.replace(/\n{3,}/g, '\n\n');
        
        // 2. 修复列表项格式
        processedContent = processedContent.replace(/^(\s*)-\s+/gm, '- ');
        processedContent = processedContent.replace(/^(\s*)\d+\.\s+/gm, '$1. ');
        
        // 3. 修复标题格式 - 确保#后有空格
        processedContent = processedContent.replace(/^(#{1,6})([^\s#])/gm, '$1 $2');
        
        // 3.1. 修复分离的###标题（将###和下一行的图标+文字合并）
        // 特殊情况：处理分割的图标（如### ⚖ 和下一行的 ️ 文字）
        processedContent = processedContent.replace(/^#{3}\s*([⚖📋🏛⚠✅💡🔄📎⚡🎯🔍📊📈📉])\s*\n\n?([️].*)/gm, '### $1$2');
        
        // 匹配###后面有换行，然后跟着图标和文字的情况
        processedContent = processedContent.replace(/^#{3}\s*\n\n?([📋🏛️⚖️⚠️✅💡🔄📎⚡🎯🔍📊📈📉].*)/gm, '### $1');
        
        // 处理###后面有空行然后跟着普通文字的情况  
        processedContent = processedContent.replace(/^#{3}\s*\n\n?([^\n#].*)/gm, '### $1');
        
        // 3.2. 修复空的图标标题（如 "### 🏛" 后面没有文字的情况）
        // 暂时注释掉这个预处理，让DOM层处理，避免误删正常图标
        // processedContent = processedContent.replace(/^(#{1,6})\s*([📋🏛️⚖️⚠️✅💡🔄📎⚡🎯🔍📊📈📉])\s*$/gm, '');
        
        // 4. 修复粗体和斜体格式
        processedContent = processedContent.replace(/\*{3,}/g, '**'); // 清理多余的星号
        
        // 5. 修复代码块格式
        processedContent = processedContent.replace(/```(\w*)\n?/g, '```$1\n');
        
        // 6. 修复表格格式 - 暂时禁用表格预处理，避免破坏表格格式
        // processedContent = processedContent.replace(/([^\n])\n(\|.*\|)/g, '$1\n\n$2');
        // processedContent = processedContent.replace(/(\|.*\|)\n([^\n|])/g, '$1\n\n$2');
        // processedContent = processedContent.replace(/\|(\s*[-:]+\s*)\|/g, '|$1|');
        
        // 7. 修复引用块格式
        processedContent = processedContent.replace(/^>\s*/gm, '> ');
        
        // 8. 清理首尾空白
        processedContent = processedContent.trim();
        
        return processedContent;
      }, []);
      
      // 优化：只在内容完成或内容长度有显著变化时执行图标修复
      useEffect(() => {
        if (!containerRef.current) return;
        
        // 检查是否需要修复图标或###
        const needsIconFix = content.length > 100 && (
          content.includes('📋') || content.includes('🏛️') || 
          content.includes('⚖️') || content.includes('⚠️') || 
          content.includes('✅') || content.includes('💡')
        );
        
        const needsHashtagFix = content.length > 50 && content.includes('###');
        
        if (needsIconFix || needsHashtagFix) {
          const timeoutId = setTimeout(() => {
            if (containerRef.current) {
              fixEmojiDisplay(containerRef.current);
            }
          }, 100); // 适当延迟，减少频繁调用
          
          return () => clearTimeout(timeoutId);
        }
      }, [content.length > 500 ? content : '', fixEmojiDisplay]); // 只在内容达到一定长度时触发
      
      // 预处理内容
      const processedContent = preprocessContent(content);
      
      return (
        <div ref={containerRef}>
          <ReactMarkdown
            remarkPlugins={[
              remarkGfm,      // GitHub风格Markdown支持
              remarkBreaks    // 换行符支持 - 关键修复！
            ]}
            components={{
              // 代码块样式
              code: ({inline, className, children, ...props}: any) => 
                inline ? 
                <code style={{
                  background: '#f5f5f5', 
                  padding: '2px 4px', 
                  borderRadius: '3px',
                  fontSize: '0.9em',
                  color: '#d73a49'
                }} {...props}>{children}</code> :
                <pre style={{
                  background: '#f6f8fa', 
                  padding: '12px', 
                  borderRadius: '6px', 
                  overflow: 'auto',
                  border: '1px solid #e1e4e8',
                  fontSize: '0.9em'
                }}><code {...props}>{children}</code></pre>,
              
              // 标题样式
              h1: ({children}) => <h1 style={{
                color: '#1890ff', 
                marginBottom: '16px',
                borderBottom: '2px solid #e1e4e8',
                paddingBottom: '8px'
              }}>{children}</h1>,
              h2: ({children}) => <h2 style={{
                color: '#1890ff', 
                marginBottom: '12px',
                borderBottom: '1px solid #e1e4e8',
                paddingBottom: '4px'
              }}>{children}</h2>,
              h3: ({children}) => <h3 style={{
                color: '#1890ff', 
                marginBottom: '8px'
              }}>{children}</h3>,
              
              // 表格样式
              table: ({children}) => <div style={{
                overflow: 'auto', 
                margin: '12px 0',
                maxWidth: '100%',
                borderRadius: '6px',
                border: '1px solid #e1e4e8'
              }}>
                <table style={{
                  width: '100%', 
                  minWidth: '600px',
                  borderCollapse: 'collapse',
                  fontSize: '0.875rem',
                  lineHeight: '1.4'
                }}>{children}</table>
              </div>,
              th: ({children}) => {
                const isNoColumn = children === 'No.' || children === 'No';
                const isCheckColumn = children === '確認' || children === 'Check' || children === '确认';
                
                return <th style={{
                  border: '1px solid #e1e4e8', 
                  padding: isNoColumn || isCheckColumn ? '10px 6px' : '10px 12px', 
                  background: '#f6f8fa',
                  fontWeight: '600',
                  textAlign: isNoColumn || isCheckColumn ? 'center' : 'left',
                  whiteSpace: 'nowrap',
                  verticalAlign: 'top',
                  width: isNoColumn ? '45px' : isCheckColumn ? '60px' : 'auto'
                }}>{children}</th>;
              },
              td: ({children}) => {
                // 检查内容是否为数字（No.列）或复选框（确认列）
                const isNumber = /^\d+$/.test(String(children).trim());
                const isCheckbox = /^☐$/.test(String(children).trim());
                
                return <td style={{
                  border: '1px solid #e1e4e8', 
                  padding: isNumber || isCheckbox ? '10px 6px' : '10px 12px',
                  verticalAlign: 'top',
                  wordBreak: 'break-word',
                  textAlign: isNumber || isCheckbox ? 'center' : 'left'
                }}>{children}</td>;
              },
              
              // 引用块样式
              blockquote: ({children}) => <blockquote style={{
                borderLeft: '4px solid #1890ff', 
                paddingLeft: '12px', 
                margin: '12px 0', 
                color: '#666',
                background: '#f8f9fa',
                padding: '8px 12px',
                borderRadius: '0 4px 4px 0'
              }}>{children}</blockquote>,
              
              // 列表样式
              ul: ({children}) => <ul style={{
                paddingLeft: '20px', 
                margin: '8px 0',
                lineHeight: '1.6'
              }}>{children}</ul>,
              ol: ({children}) => <ol style={{
                paddingLeft: '20px', 
                margin: '8px 0',
                lineHeight: '1.6'
              }}>{children}</ol>,
              li: ({children}) => <li style={{
                marginBottom: '4px'
              }}>{children}</li>,
              
              // 段落样式
              p: ({children}) => <p style={{
                margin: '8px 0',
                lineHeight: '1.6'
              }}>{children}</p>,
              
              // 强调样式
              strong: ({children}) => <strong style={{
                fontWeight: 'bold',
                color: '#24292e'
              }}>{children}</strong>,
              em: ({children}) => <em style={{
                fontStyle: 'italic',
                color: '#586069'
              }}>{children}</em>,
              
              // 链接样式
              a: ({children, href}) => <a 
                href={href} 
                style={{
                  color: '#0366d6',
                  textDecoration: 'none'
                }}
                onMouseEnter={(e) => (e.target as HTMLElement).style.textDecoration = 'underline'}
                onMouseLeave={(e) => (e.target as HTMLElement).style.textDecoration = 'none'}
              >{children}</a>,
              
              // 分割线样式
              hr: () => <hr style={{
                border: 'none',
                borderTop: '1px solid #e1e4e8',
                margin: '16px 0'
              }} />
            }}
          >
            {processedContent}
          </ReactMarkdown>
        </div>
      );
    };
    return MarkdownComponent;
  }, [message.content, message.isStreaming]);

  /**
   * 复制消息内容
   */
  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(message.content);
      // TODO: 显示成功提示
    } catch (error) {
      console.error('复制失败:', error);
    }
  }, [message.content]);

  /**
   * 语音播放
   */
  const handleSpeak = useCallback(() => {
    if (isPlaying) {
      speechSynthesis.cancel();
      setIsPlaying(false);
    } else {
      const utterance = new SpeechSynthesisUtterance(message.content);
      utterance.lang = 'ja-JP';
      utterance.onend = () => setIsPlaying(false);
      utterance.onerror = () => setIsPlaying(false);
      
      speechSynthesis.speak(utterance);
      setIsPlaying(true);
    }
  }, [message.content, isPlaying]);

  /**
   * 点赞/踩
   */
  const handleLike = useCallback(() => {
    // TODO: 实现点赞逻辑
    // console.log('点赞消息:', message.id);
  }, [message.id]);

  const handleDislike = useCallback(() => {
    // TODO: 实现踩的逻辑
    // console.log('踩消息:', message.id);
  }, [message.id]);

  /**
   * 渲染澄清问题
   */
  const renderClarificationMessage = () => {
    const completeness = message.caseInfo?.completeness_score || 0;
    const completenessPercent = Math.round(completeness * 100);
    
    return (
      <div>
        <OptimizedMarkdown content={message.content} />
        
        <ClarificationHint className="clarification-hint">
          <BookOutlined />
          <div>
            <div style={{ marginBottom: '8px' }}>
              <Text strong>💡 {t('chat.message.analysisImprovementTitle')}</Text>
            </div>
            <Text>{message.analysisCapability}</Text>
            
            {completeness > 0 && (
              <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                {t('chat.message.currentCompleteness')} {completenessPercent}%
              </div>
            )}
            
            {completeness >= 0.4 && (
              <ForceAnalysisOption className="force-analysis-option">
                <span>{t('chat.message.forceAnalysisWarning')}</span>
                <span className="warning-text">{t('chat.message.analysisSuboptimal')}</span>
                <Button 
                  size="small" 
                  type="link" 
                  className="force-btn"
                  onClick={onForceStartAnalysis}
                >
                  {t('chat.message.startAnalysis')}
                </Button>
              </ForceAnalysisOption>
            )}
          </div>
        </ClarificationHint>
      </div>
    );
  };

  /**
   * 渲染确认消息
   */
  const renderConfirmationMessage = () => {
    return (
      <div>
        <OptimizedMarkdown content={message.content} />
        
        <ConfirmationActions>
          <Button 
            type="primary" 
            icon={<CheckOutlined />}
            onClick={onConfirmCaseInfo}
          >
            {t('chat.message.startAnalysis')}
          </Button>
          <Button 
            icon={<EditOutlined />}
            onClick={onModifyCaseInfo}
          >
            {t('chat.message.supplementInfo')}
          </Button>
          <Button 
            icon={<EditOutlined />}
            onClick={onModifyCaseInfo}
          >
            {t('chat.message.modifyInfo')}
          </Button>
        </ConfirmationActions>
      </div>
    );
  };

  /**
   * 渲染策略选择消息
   */
  const renderStrategySelectionMessage = () => {
    if (!message.strategies || !onStrategySelect) {
      return null;
    }
    // console.log('🔍 ChatMessage传递给StrategySelectionMessage:', {
    //   showDocumentSupport: message.showDocumentSupport,
    //   messageId: message.id,
    //   messageType: message.type
    // });
    
    // console.log('🔍 策略选择消息的currentStrategy:', {
    //   currentStrategy: message.currentStrategy,
    //   messageId: message.id,
    //   messageType: message.type
    // });
    
    return (
      <StrategySelectionMessage
        strategies={message.strategies}
        message={message.content}
        onStrategySelect={onStrategySelect}
        selectedStrategy={message.currentStrategy}
        showDocumentSupport={message.showDocumentSupport}
      />
    );
  };

  /**
   * 渲染策略修改提示消息
   */
  const renderStrategyModificationPrompt = () => {
    if (!message.currentStrategy || !onModificationDecision) return null;
    
    return (
      <StrategyModificationPrompt
        message={message.content}
        currentStrategy={message.currentStrategy}
        onComplete={() => onModificationDecision('complete')}
        onModify={() => onModificationDecision('modify')}
        onReselect={onReselectStrategy}
        availableStrategies={message.strategies}
      />
    );
  };

  /**
   * 渲染普通消息
   */
  const renderNormalMessage = () => {
    return (
      <div>
        <OptimizedMarkdown content={message.content} />
        
        {message.files && message.files.length > 0 && (
          <div>
            {message.files.map(file => (
              <FileAttachment key={file.id} className="file-attachment">
                <span>📎 {file.name}</span>
                <span>({Math.round(file.size / 1024)}KB)</span>
              </FileAttachment>
            ))}
          </div>
        )}
      </div>
    );
  };

  /**
   * 渲染流式指示器
   */
  const renderStreamingIndicator = () => {
    // 只有当消息正在流式传输且尚未完成时才显示指示器
    // 对于分析类型的消息，不显示"考え中..."指示器
    if (!message.isStreaming || message.isStreamingComplete || message.type === 'analysis') return null;
    
    return (
      <StreamingIndicator>
        <div className="streaming-dots" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <LoadingOutlined style={{ animation: 'spin 1s linear infinite' }} />
          <span>{t('chat.thinking')}</span>
          <span className="dot">.</span>
          <span className="dot">.</span>
          <span className="dot">.</span>
        </div>
      </StreamingIndicator>
    );
  };

  /**
   * 渲染消息操作按钮
   */
  const renderMessageActions = () => {
    // 只有助手消息且不在流式传输中或已完成时才显示操作按钮
    if (message.role === 'user' || (message.isStreaming && !message.isStreamingComplete)) return null;
    
    return (
      <MessageActions className="message-actions">
        <button className="action-btn" onClick={handleCopy} title={t('chat.copy')}>
          <CopyOutlined />
        </button>
        <button 
          className="action-btn" 
          onClick={handleSpeak} 
          title={isPlaying ? t('chat.stopAudio') : t('chat.playAudio')}
        >
          {isPlaying ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
        </button>
        <button className="action-btn" onClick={handleLike} title={t('chat.like')}>
          <LikeOutlined />
        </button>
        <button className="action-btn" onClick={handleDislike} title={t('chat.dislike')}>
          <DislikeOutlined />
        </button>
      </MessageActions>
    );
  };

  /**
   * 渲染消息内容
   */
  const renderMessageContent = () => {
    switch (message.type) {
      case 'clarification':
        return renderClarificationMessage();
      case 'confirmation':
        return renderConfirmationMessage();
      case 'strategy_selection':
        return renderStrategySelectionMessage();
      case 'strategy_modification_prompt':
        return null; // 不渲染"分析完了"对话
      case 'analysis':
      case 'normal':
      default:
        return renderNormalMessage();
    }
  };

  return (
    <MessageContainer $isUser={message.role === 'user'}>
      <Avatar $isUser={message.role === 'user'}>
        {message.role === 'user' ? <UserOutlined /> : <BookOutlined />}
      </Avatar>
      
      <div style={{ 
        flex: 1, 
        maxWidth: message.role === 'user' 
          ? '70%' 
          : isMobile 
            ? '85%'  // 移动端AI回复85%
            : '85%', // PC端AI回复85%
        minWidth: '200px' /* 确保最小宽度 */
      }}>
        <MessageContent $isUser={message.role === 'user'} className="chat-message-content">
          {renderMessageContent()}
          {renderStreamingIndicator()}
        </MessageContent>
        
        {renderMessageActions()}
      </div>
    </MessageContainer>
  );
};

// 添加流式指示器的CSS动画
const globalStyles = `
  @keyframes dots {
    0%, 20% { opacity: 0; }
    50% { opacity: 1; }
    80%, 100% { opacity: 0; }
  }
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  
  .streaming-dots .dot:nth-child(3) {
    animation: dots 1.4s infinite 0.2s;
  }
  
  .streaming-dots .dot:nth-child(4) {
    animation: dots 1.4s infinite 0.4s;
  }
  
  .streaming-dots .dot:nth-child(5) {
    animation: dots 1.4s infinite 0.6s;
  }
`;

// 将样式注入到页面
if (typeof document !== 'undefined') {
  const styleElement = document.createElement('style');
  styleElement.textContent = globalStyles;
  document.head.appendChild(styleElement);
}

export default ChatMessage; 